import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useSession } from "../../session/SessionContext";
import { PermissionDeniedState } from "../states";

/**
 * The route-level permission gate every phase 21 (`AIA-81`) administration screen renders itself
 * through. A person who is not signed in, or is signed in but lacks the required permission(s), sees
 * an explicit {@link PermissionDeniedState} — never an empty list — which is the success criterion
 * this phase's brief states most directly ("a denied state renders as denied, never as empty").
 *
 * Used for `routes/admin/SyncStatusPage.tsx` in particular to gate a screen whose one API call
 * (`GET /api/status/sync`) is itself `AllowAnonymousAccess`: the API being public does not mean the
 * *screen* is, and this component is what keeps an unclassified visitor from reaching it.
 *
 * @param props.permissions - The permission key(s) required.
 * @param props.mode - `"all"` (default) requires every key; `"any"` requires at least one.
 * @param props.title - Overrides the default denied-state heading.
 * @param props.description - Overrides the default denied-state description, which otherwise names the missing permission(s).
 */
export function AdminGate({
  permissions,
  mode = "all",
  title,
  description,
  children,
}: {
  permissions: string[];
  mode?: "all" | "any";
  title?: string;
  description?: string;
  children: ReactNode;
}): React.JSX.Element {
  const { t } = useTranslation();
  const { snapshot } = useSession();

  const held = snapshot.status === "signed-in" ? snapshot.permissions : [];
  const hasAccess = snapshot.status === "signed-in" && (mode === "all" ? permissions.every((p) => held.includes(p)) : permissions.some((p) => held.includes(p)));

  if (!hasAccess) {
    return (
      <PermissionDeniedState
        title={title ?? t("admin.denied.title")}
        description={description ?? t("admin.denied.description", { permissions: permissions.join(", ") })}
      />
    );
  }

  return <>{children}</>;
}
